import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../services/apiService';
import { registerTrackingProps } from '../utils/analytics';
import { clampMs, createOnboardingTracker } from '../utils/onboardingTrack';
import type {
  OnboardingPublicConfig, OnboardingResult, OnbEntry, OnbOutcome, OnboardingSavePayload,
} from '../types/onboarding';
import { resolveOnboardingCopy } from '../types/onboarding';

/**
 * First run (v2, spec 13 Oct 2026): two short steps, both skippable.
 *
 *   Step 1  pick an industry, or "Skip for now" → Cosmos with 3 random cards.
 *   Step 2  pick one of that industry's chips (no free-text box). Explore
 *           Cosmos appears as soon as a chip is picked; "Skip for now" →
 *           Cosmos with the industry only.
 *
 * No preview cards, no count, no in-modal trial: the Explorer lands on their
 * 3 precomputed picks and the upgrade block (DirectSearchPage). Everything the
 * modal shows — industries, their chips, every line of copy — comes from the
 * admin-editable public config (Admin → Onboarding), preloaded by App, so a
 * chip click never waits on the network.
 *
 * Every state change is saved immediately (POST /onboarding/save with an
 * `event`) and tracked (utils/onboardingTrack). The saves also carry the
 * pre-v2 fields, so an older server still records the choice as before.
 *
 * The flow-v1 modal is kept verbatim as OnboardingModalLegacy.
 */

interface OnboardingModalProps {
  config: OnboardingPublicConfig;
  entry: OnbEntry;
  /** App's currentPlanCode, for attribution only. */
  plan: string;
  /** Called after the final save resolves (or 2 s, whichever is first). */
  onFinish: (r: OnboardingResult) => void;
  /** Admin → Onboarding → Settings: render inline (not over the page), with
   *  no saves and no tracking. `industry`/`keyword` preselect step 2. */
  preview?: { industry?: string; keyword?: string } | null;
}

const FINAL_SAVE_TIMEOUT_MS = 2000;

const OnboardingModal: React.FC<OnboardingModalProps> = ({ config, entry, plan, onFinish, preview }) => {
  const isPreview = !!preview;
  const userId = apiService.getStoredUserId() || '';
  const copy = useMemo(() => resolveOnboardingCopy(config?.copy), [config]);
  const industries = config?.industries || [];
  const flow = config?.flow || 'v2';
  const configVersion = config?.config_version || '';
  // A client-built fallback config (types/onboarding.fallbackOnboardingConfig)
  // has no version and no chips: fetch each industry's chips instead.
  const isFallbackConfig = !configVersion;

  // One tracker per mount: ob_id + clock. Created once, never re-created.
  const [tracker] = useState(() => createOnboardingTracker(flow, entry, !isPreview && !!userId));

  const [industry, setIndustry] = useState(preview?.industry || '');
  const [keyword, setKeyword] = useState(preview?.keyword || '');
  const [fetchedChips, setFetchedChips] = useState<Record<string, string[]>>({});
  const [chipsLoading, setChipsLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  // Timers and change counters live in refs: they feed events, not rendering.
  const lastIndustryAt = useRef<number>(Date.now());
  const industryPicks = useRef(0);
  const keywordPicks = useRef(0);
  const step2Viewed = useRef<string>('');
  const finished = useRef(false);

  useEffect(() => {
    if (!isPreview) return;
    setIndustry(preview?.industry || '');
    setKeyword(preview?.keyword || '');
  }, [isPreview, preview?.industry, preview?.keyword]);

  const entryFor = (name: string) => industries.find(i => i.name === name);
  const chipsFor = (name: string): string[] => {
    const row = entryFor(name);
    const own = (row?.chips || []).filter(Boolean).slice(0, 6);
    if (own.length || !isFallbackConfig) return own;
    return (fetchedChips[name] || []).slice(0, 6);
  };
  const chips = industry ? chipsFor(industry) : [];
  const chipsSource = industry
    ? (entryFor(industry)?.chips?.length ? entryFor(industry)!.chips_source
       : (fetchedChips[industry]?.length ? 'corpus' : 'none'))
    : 'none';
  const step: 1 | 2 = industry ? 2 : 1;

  // ---- server writes -----------------------------------------------------
  const common = (): Partial<OnboardingSavePayload> => ({
    ob_id: tracker.ob_id, entry, plan: plan || undefined, flow,
    config_version: configVersion || undefined,
  });

  const reportError = (stage: 'save' | 'chips', status?: number | null, step_?: 1 | 2,
                       industry_?: string | null) => {
    tracker.track('onboarding_error', { stage, status: status ?? null, step: step_ ?? step });
    if (!userId || isPreview) return;
    void apiService.logOnboardingEvent(userId, {
      ob_id: tracker.ob_id, event: 'error', step: step_ ?? step, industry: industry_ ?? (industry || null),
      entry, plan: plan || undefined, flow, config_version: configVersion || undefined,
      props: { stage, status: status ?? null },
    }).catch(() => undefined);
  };

  /** Fire-and-forget save; failures are tracked, never shown. */
  const save = (payload: OnboardingSavePayload): Promise<void> => {
    if (!userId || isPreview) return Promise.resolve();
    return apiService.saveOnboarding(userId, { ...common(), ...payload })
      .then(res => { if (!res.success) reportError('save', res.status ?? null, payload.step, payload.industry); })
      .catch(() => reportError('save', null, payload.step, payload.industry));
  };

  // ---- shown + step 1 view (once per mount) -------------------------------
  useEffect(() => {
    if (isPreview || !userId) return;
    tracker.track('onboarding_shown', {
      plan: plan || null, industries_count: industries.length, config_version: configVersion || null,
    });
    void apiService.logOnboardingEvent(userId, {
      ob_id: tracker.ob_id, event: 'shown', step: 1, entry, plan: plan || undefined, flow,
      config_version: configVersion || undefined,
      props: { industries_count: industries.length },
    }).catch(() => undefined);
    tracker.track('onboarding_step_view', { step: 1 });
    // Once per mount by design.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- fallback chips (only when the public config could not be loaded) ---
  useEffect(() => {
    if (!industry || !isFallbackConfig || isPreview) return;
    if ((entryFor(industry)?.chips || []).length || fetchedChips[industry]) return;
    if (industry === 'Others') { setFetchedChips(m => ({ ...m, [industry]: [] })); return; }
    let cancelled = false;
    setChipsLoading(true);
    apiService.getOnboardingKeywords(industry).then(res => {
      if (cancelled) return;
      setChipsLoading(false);
      if (!res.success || !res.data?.success) reportError('chips', res.status ?? null, 2, industry);
      const list = res.success && res.data?.success && Array.isArray(res.data.keywords)
        ? (res.data.keywords as string[]) : [];
      setFetchedChips(m => ({ ...m, [industry]: list }));
    }).catch(() => { if (!cancelled) { setChipsLoading(false); setFetchedChips(m => ({ ...m, [industry]: [] })); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [industry, isFallbackConfig]);

  // ---- step 2 view (once per industry, after its chips are known) ---------
  useEffect(() => {
    if (isPreview || !industry || chipsLoading) return;
    if (isFallbackConfig && !entryFor(industry)?.chips?.length && !(industry in fetchedChips)) return;
    if (step2Viewed.current === industry) return;
    step2Viewed.current = industry;
    tracker.track('onboarding_step_view', { step: 2, industry, chips, chips_source: chipsSource });
    if (!chips.length) {
      tracker.track('onboarding_chips_empty', { industry });
      if (userId) {
        void apiService.logOnboardingEvent(userId, {
          ob_id: tracker.ob_id, event: 'chips_empty', step: 2, industry, entry,
          plan: plan || undefined, flow, config_version: configVersion || undefined,
        }).catch(() => undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [industry, chipsLoading, fetchedChips]);

  // ---- actions --------------------------------------------------------------
  const changeIndustry = (next: string) => {
    if (isPreview) { setIndustry(next); setKeyword(''); return; }
    // The empty placeholder is not a choice: no event, and the keyword goes.
    if (!next) { setIndustry(''); setKeyword(''); return; }
    if (next === industry) return;
    const now = Date.now();
    industryPicks.current += 1;
    // Time on step 1 = open → this (latest) industry choice.
    const msOnStep = clampMs(now - tracker.openedAt);
    tracker.track('onboarding_select_industry', {
      industry: next,
      industry_index: industries.findIndex(i => i.name === next),
      previous_industry: industry || null,
      change_n: industryPicks.current,
      ms_on_step: msOnStep,
    });
    lastIndustryAt.current = now;
    setIndustry(next);
    setKeyword('');
    void save({ event: 'industry', step: 1, industry: next, ms_on_step: msOnStep,
                industry_changes: Math.max(0, industryPicks.current - 1) });
  };

  const pickKeyword = (chip: string, index: number) => {
    if (isPreview) { setKeyword(chip); return; }
    if (chip === keyword || busy) return;
    keywordPicks.current += 1;
    const msOnStep = clampMs(Date.now() - lastIndustryAt.current);
    tracker.track('onboarding_select_keyword', {
      industry, keyword: chip, chip_index: index, previous_keyword: keyword || null,
      change_n: keywordPicks.current, ms_on_step: msOnStep, source: 'chip',
    });
    setKeyword(chip);
    void save({ event: 'keyword', step: 2, industry, keyword: chip, chip_index: index,
                ms_on_step: msOnStep, keyword_changes: Math.max(0, keywordPicks.current - 1) });
  };

  /** The last save is awaited (≤2 s) so the landing reads the saved choice. */
  const finish = async (outcome: OnbOutcome, viaSkip: boolean) => {
    if (isPreview || finished.current) return;
    finished.current = true;
    setBusy(true);
    const now = Date.now();
    const atStep: 1 | 2 = industry ? 2 : 1;
    const msOnStep = clampMs(now - (atStep === 2 ? lastIndustryAt.current : tracker.openedAt));
    const msTotal = clampMs(now - tracker.openedAt);
    const chipIndex = keyword ? chips.indexOf(keyword) : -1;
    const industryChanges = Math.max(0, industryPicks.current - 1);
    const keywordChanges = Math.max(0, keywordPicks.current - 1);
    const result: OnboardingResult = {
      outcome,
      industry: outcome === 'skipped' ? null : (industry || null),
      keyword: outcome === 'industry_keyword' ? (keyword || null) : null,
      ob_id: tracker.ob_id,
    };
    let payload: OnboardingSavePayload;
    if (viaSkip) {
      tracker.track('onboarding_click_skip', {
        step: atStep, industry: result.industry, outcome, ms_on_step: msOnStep, ms_total: msTotal,
      });
      payload = {
        event: 'skip', step: atStep, outcome, skipped: true,
        ...(result.industry ? { industry: result.industry } : {}),
        ms_on_step: msOnStep, ms_total: msTotal,
        industry_changes: industryChanges, keyword_changes: keywordChanges,
      };
    } else {
      tracker.track('onboarding_click_explore_cosmos', {
        industry: result.industry, keyword: result.keyword,
        chip_index: chipIndex >= 0 ? chipIndex : null, outcome,
        ms_on_step: msOnStep, ms_total: msTotal,
        industry_changes: industryChanges, keyword_changes: keywordChanges,
      });
      payload = {
        event: 'explore', step: 2, outcome, completed: true,
        industry: result.industry || undefined,
        ...(result.keyword ? { keyword: result.keyword } : {}),
        chip_index: chipIndex >= 0 ? chipIndex : null,
        ms_on_step: msOnStep, ms_total: msTotal,
        industry_changes: industryChanges, keyword_changes: keywordChanges,
      };
    }
    // Every later PostHog event can then be broken down by the choice.
    registerTrackingProps({
      onb_outcome: outcome, onb_industry: result.industry, onb_keyword: result.keyword, onb_flow: flow,
    });
    await Promise.race([
      save(payload),
      new Promise<void>(resolve => setTimeout(resolve, FINAL_SAVE_TIMEOUT_MS)),
    ]);
    onFinish(result);
  };

  const skip = () => finish(industry ? 'industry_only' : 'skipped', true);
  const explore = () => finish(keyword ? 'industry_keyword' : 'industry_only', false);

  // ---- render ---------------------------------------------------------------
  const showExplore = !!industry && (!!keyword || (!chipsLoading && chips.length === 0));
  const titleId = isPreview ? undefined : 'onboarding-title';

  const card = (
    <div role={isPreview ? undefined : 'dialog'} aria-modal={isPreview ? undefined : true}
      aria-labelledby={titleId}
      className="bg-white rounded-2xl shadow-lg w-full max-w-md p-5 sm:p-6 max-h-[90vh] overflow-y-auto">
      {/* Two-segment progress rail */}
      <div className="flex gap-2 mb-4" aria-hidden="true">
        <span className="h-1 flex-1 rounded-full bg-primary-500" />
        <span className={`h-1 flex-1 rounded-full ${step === 2 ? 'bg-primary-500' : 'bg-primary-100'}`} />
      </div>
      <div className="text-[11px] font-bold tracking-widest text-primary-700 uppercase mb-1">
        Step {step} of 2
      </div>

      <h2 id={titleId} className="text-xl font-bold text-grayscale-1300 font-grotesk">
        {step === 2 ? copy.step2_title : copy.step1_title}
      </h2>
      <p className="text-sm text-grayscale-1200 mt-1 mb-4">
        {step === 2 ? copy.step2_sub : copy.step1_sub}
      </p>

      {/* The industry stays changeable on step 2. */}
      <label htmlFor={isPreview ? undefined : 'onboarding-industry'} className="text-xs text-grayscale-1100">
        {copy.industry_label}
      </label>
      <select id={isPreview ? undefined : 'onboarding-industry'} value={industry}
        disabled={busy}
        onChange={e => changeIndustry(e.target.value)}
        className="h-11 px-2.5 mt-1 mb-4 rounded-md border-primary-300 border-[0.5px] bg-white text-sm w-full">
        <option value="">{copy.industry_placeholder}</option>
        {industries.map(i => <option key={i.name} value={i.name}>{i.name}</option>)}
      </select>

      {industry && (
        <>
          {chipsLoading ? (
            <div className="text-sm text-grayscale-1100 py-2">Loading keywords…</div>
          ) : chips.length > 0 ? (
            <>
              <div className="text-xs text-grayscale-1100 mb-1">{copy.keyword_label}</div>
              <div className="grid grid-cols-3 gap-2 mb-3" role="group" aria-label={copy.keyword_label}>
                {chips.map((c, i) => {
                  const on = c === keyword;
                  return (
                    <button key={c} type="button" aria-pressed={on} disabled={busy}
                      onClick={() => pickKeyword(c, i)}
                      lang="en"
                      className={`min-h-[52px] px-1.5 sm:px-2 py-2 rounded-xl border text-[11px] sm:text-xs leading-snug text-center [hyphens:auto] break-words disabled:opacity-60 ${
                        on
                          ? 'border-primary-500 bg-primary-100 text-grayscale-1300 font-medium'
                          : 'border-primary-200 bg-white text-grayscale-1300 hover:border-primary-400 hover:bg-primary-100/40'}`}>
                      {c}
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <p className="text-sm text-grayscale-1200 mb-3">{copy.no_chips_note}</p>
          )}
        </>
      )}

      {showExplore ? (
        <button type="button" onClick={explore} disabled={busy}
          className="w-full mt-1 py-3 rounded-full font-grotesk text-sm font-medium bg-primary-600 hover:bg-primary-700 text-grayscale-100 disabled:opacity-60">
          {copy.explore_cta}
        </button>
      ) : (
        <button type="button" onClick={skip} disabled={busy || (!!industry && chipsLoading)}
          className="w-full text-sm text-grayscale-1200 hover:text-grayscale-1300 py-2 mt-1 disabled:opacity-60">
          {copy.skip_label}
        </button>
      )}
    </div>
  );

  if (isPreview) return <div className="flex justify-center">{card}</div>;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      {card}
    </div>
  );
};

export default OnboardingModal;
